import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, FileButton, Group, Image, Paper, Stack, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconPhotoUp, IconTrash } from '@tabler/icons-react';
import { isAxiosError, type AxiosInstance } from 'axios';
import { apiClient } from '../api/client';

// Shared image/logo uploader: picks a file, POSTs it as multipart to the
// uploads endpoint (blob storage, per-tenant folder), and hands the returned
// URL to the host form — the URL is then persisted through the entity's own
// save endpoint. Used by the item drawer, business settings and the platform
// tenant-profile modal (the latter passes its own uploadPath).

interface Props {
  label: string;
  value: string | null;
  onChange: (url: string | null) => void;
  /** Tenant-scoped kind for /api/uploads/image. Ignored when uploadPath is set. */
  kind?: 'items' | 'logo';
  /** Override endpoint (platform admin uploads into a target tenant's folder). */
  uploadPath?: string;
  /** Axios client override — the platform portal has its own auth client. */
  api?: AxiosInstance;
  disabled?: boolean;
}

export function ImageUploadInput({
  label,
  value,
  onChange,
  kind = 'items',
  uploadPath,
  api,
  disabled,
}: Props) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);

  const upload = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const path = uploadPath ?? `/api/uploads/image?kind=${kind}`;
      const response = await (api ?? apiClient).post<{ url: string }>(path, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(response.data.url);
    } catch (err) {
      const status = isAxiosError(err) ? err.response?.status : undefined;
      notifications.show({
        color: 'red',
        title: t('common.upload.failed'),
        message:
          status === 503
            ? t('common.upload.notConfigured')
            : (isAxiosError(err)
                ? ((err.response?.data as { title?: string } | undefined)?.title ?? err.message)
                : t('common.toasts.errorBody')),
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Paper p="md" radius="sm" withBorder>
      <Stack gap="xs">
        <div>
          <Text fw={500} fz="sm" c="var(--wheels-ink-strong)">
            {label}
          </Text>
          <Text fz="xs" c="var(--wheels-ink-muted)">
            {t('common.upload.hint')}
          </Text>
        </div>
        <Group align="center" gap="md">
          {value ? (
            <Image src={value} h={72} w={72} fit="contain" radius="sm" bg="var(--wheels-bg)" />
          ) : null}
          <FileButton onChange={upload} accept="image/jpeg,image/png,image/webp" disabled={disabled}>
            {(props) => (
              <Button
                {...props}
                variant="light"
                loading={uploading}
                leftSection={<IconPhotoUp size={16} stroke={1.8} />}
              >
                {value ? t('common.upload.replace') : t('common.upload.choose')}
              </Button>
            )}
          </FileButton>
          {value ? (
            <Button
              variant="subtle"
              color="red"
              leftSection={<IconTrash size={14} stroke={1.8} />}
              onClick={() => onChange(null)}
              disabled={disabled || uploading}
            >
              {t('common.upload.remove')}
            </Button>
          ) : null}
        </Group>
      </Stack>
    </Paper>
  );
}
